// Beds24Feed — activity strip at the top of Reception dashboard.
// Pairs with the reception push notifications: push tells them, the strip
// lets them scan the last 10 events at a glance.
import React, { useCallback, useEffect, useState } from 'react';
import { Activity, RefreshCw, Loader2, User, Ban, Edit3, Star } from 'lucide-react';
import { apiCall } from './_shared';

const T = {
  en: {
    title: 'Live front desk',
    empty: 'No booking events yet. When Beds24 sends the next one, it will appear here.',
    new: 'New', modified: 'Modified', cancelled: 'Cancelled',
    ago: (m) => m < 1 ? 'just now' : (m < 60 ? `${m}m ago` : (m < 1440 ? `${Math.floor(m/60)}h ago` : `${Math.floor(m/1440)}d ago`)),
  },
  me: {
    title: 'Recepcija u živo',
    empty: 'Još nema događaja. Kada Beds24 pošalje novi, pojaviće se ovdje.',
    new: 'Nova', modified: 'Izmijenjeno', cancelled: 'Otkazano',
    ago: (m) => m < 1 ? 'upravo sada' : (m < 60 ? `prije ${m}m` : (m < 1440 ? `prije ${Math.floor(m/60)}h` : `prije ${Math.floor(m/1440)}d`)),
  },
  sq: {
    title: 'Recepsioni në kohë reale',
    empty: 'Ende s\'ka ngjarje. Kur Beds24 dërgon një, do të shfaqet këtu.',
    new: 'E re', modified: 'Modifikuar', cancelled: 'Anuluar',
    ago: (m) => m < 1 ? 'tani' : (m < 60 ? `${m}m më parë` : (m < 1440 ? `${Math.floor(m/60)}h më parë` : `${Math.floor(m/1440)}d më parë`)),
  },
};

const classifyAction = (evt) => {
  const st = (evt.status || '').toLowerCase();
  const ac = (evt.action || '').toLowerCase();
  if (st.startsWith('cancel') || ac.includes('cancel')) return 'cancelled';
  if (ac.includes('modif') || ac.includes('update')) return 'modified';
  return 'new';
};

const Beds24Feed = ({ lang = 'en' }) => {
  const t = T[lang] || T.en;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [unread, setUnread] = useState(0);

  const readMarkerKey = 'beds24_feed_last_read';
  const getLastRead = () => {
    try { return localStorage.getItem(readMarkerKey) || ''; } catch { return ''; }
  };
  const setLastRead = (iso) => {
    try { localStorage.setItem(readMarkerKey, iso); } catch {}
  };

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const json = await apiCall('/api/admin/reception/beds24-feed?limit=10');
      const list = json.items || [];
      setItems(list);
      const last = getLastRead();
      const fresh = last ? list.filter(i => (i.received_at || '') > last).length : list.length;
      setUnread(fresh);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);   // refresh every minute — cheap
    return () => clearInterval(iv);
  }, [load]);

  const markRead = () => {
    if (items.length === 0) return;
    setLastRead(items[0].received_at || new Date().toISOString());
    setUnread(0);
  };

  // Hoist rendered rows OUT of JSX to sidestep Babel plugin stack overflow.
  const now = Date.now();
  const rows = items.map((evt) => {
    const kind = classifyAction(evt);
    const iconMap = { new: <Star className="w-4 h-4" />, modified: <Edit3 className="w-4 h-4" />, cancelled: <Ban className="w-4 h-4" /> };
    const clsMap = {
      new: 'bg-green-50 border-green-200 text-green-800',
      modified: 'bg-amber-50 border-amber-200 text-amber-800',
      cancelled: 'bg-red-50 border-red-200 text-red-800',
    };
    const receivedAt = evt.received_at ? new Date(evt.received_at) : null;
    const minutesAgo = receivedAt ? Math.max(0, Math.floor((now - receivedAt.getTime()) / 60000)) : null;
    return (
      <div key={(evt.id || '') + (evt.received_at || '') } className={`flex-shrink-0 w-72 rounded-lg border p-3 ${clsMap[kind]}`} data-testid={`beds24-event-${evt.id || 'x'}`}>
        <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider">
          <span className="inline-flex items-center gap-1">{iconMap[kind]} {t[kind]}</span>
          {minutesAgo !== null && <span className="opacity-70">{t.ago(minutesAgo)}</span>}
        </div>
        <p className="font-semibold text-sm mt-1 truncate flex items-center gap-1">
          <User className="w-3.5 h-3.5 flex-shrink-0" /> {evt.guest || 'Guest'}
        </p>
        <p className="text-xs opacity-80 mt-0.5">
          {evt.check_in && evt.check_out ? `${evt.check_in} → ${evt.check_out}` : '—'}
          {evt.total ? ` · €${evt.total}` : ''}
        </p>
        <p className="text-[11px] opacity-60 mt-1">{evt.source || 'direct'}</p>
      </div>
    );
  });

  return (
    <div className="bg-white rounded-xl border p-4" data-testid="beds24-feed">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <Activity className="w-5 h-5 text-orange-500" /> {t.title}
          {unread > 0 && (
            <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold" data-testid="beds24-unread-badge">
              {unread}
            </span>
          )}
        </h3>
        <div className="flex gap-2">
          {unread > 0 && (
            <button onClick={markRead} className="text-xs text-orange-600 hover:text-orange-700 font-semibold" data-testid="beds24-mark-read">
              Mark read
            </button>
          )}
          <button onClick={load} disabled={loading} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
      {err && <p className="text-xs text-red-600 mb-2">{err}</p>}
      {items.length === 0 && !loading ? (
        <p className="text-sm text-gray-400">{t.empty}</p>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1">{rows}</div>
      )}
    </div>
  );
};

export default Beds24Feed;
